import React, { useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  SubCondition,
  ConditionQuantifier,
  HUB_MAX_SUB_CONDITIONS,
} from 'dorian-shared/types/branch/HubOption';
import type { ReplayCondition } from 'dorian-shared/types/branch/HubOption';
import { MemoryDTO } from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { DragHandle } from 'components/ui/DragHandle/DragHandle';
import { createEmptySubCondition } from '../../stateConditionDefaults';
import { SubConditionRow } from '../SubConditionRow';
import { ReplayResultRow } from './ReplayResultRow';
import { BranchSelectItem } from '../../shared/types';
import './ReplayConditionCard.scss';

interface ReplayConditionCardProps {
  condition: ReplayCondition;
  memoryVariables: MemoryDTO[];
  branches: BranchSelectItem[];
  currentBranchId: number | null;
  isDisabled: boolean;
  isValid: boolean;
  onChange: (updated: ReplayCondition) => void;
  onRemove: () => void;
}

export function ReplayConditionCard({
  condition,
  memoryVariables,
  branches,
  currentBranchId,
  isDisabled,
  isValid,
  onChange,
  onRemove,
}: ReplayConditionCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: condition.uuid,
    disabled: isDisabled,
    transition: {
      duration: 250,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    },
  });

  const style = {
    transform: isDisabled ? undefined : CSS.Transform.toString(transform),
    transition: isDisabled ? undefined : transition,
    boxShadow: isDragging
      ? '-1px 0 15px 0 rgba(34, 33, 81, 0.01), 0px 15px 15px 0 rgba(34, 33, 81, 0.25)'
      : undefined,
    opacity: isDragging ? 0.95 : 1,
  };

  // Defensive: ensure at least 1 sub-condition exists (corrupted data guard)
  const subConditions = condition.subConditions.length > 0
    ? condition.subConditions
    : [createEmptySubCondition()];
  const isCompound = subConditions.length >= 2;
  const canAddSub = subConditions.length < HUB_MAX_SUB_CONDITIONS;

  const handleQuantifierToggle = useCallback(
    (q: ConditionQuantifier) => {
      onChange({ ...condition, quantifier: q });
    },
    [condition, onChange],
  );

  const handleSubConditionChange = useCallback(
    (index: number, updated: SubCondition) => {
      const newSubs = [...subConditions];
      newSubs[index] = updated;
      onChange({ ...condition, subConditions: newSubs });
    },
    [condition, subConditions, onChange],
  );

  const handleSubConditionRemove = useCallback(
    (index: number) => {
      const newSubs = subConditions.filter((_, i) => i !== index);
      onChange({
        ...condition,
        subConditions: newSubs,
        quantifier: newSubs.length <= 1 ? ConditionQuantifier.All : condition.quantifier,
      });
    },
    [condition, subConditions, onChange],
  );

  const handleAddSubCondition = useCallback(() => {
    onChange({
      ...condition,
      subConditions: [...subConditions, createEmptySubCondition()],
    });
  }, [condition, subConditions, onChange]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`replay-condition-card${isDragging ? ' replay-condition-card--dragging' : ''}${!isValid ? ' replay-condition-card--error' : ''}`}
      data-testid="replay-condition-card"
    >
      {/* Header row: drag handle + condition header + remove */}
      <div className="replay-condition-card__header">
        <DragHandle
          attributes={attributes}
          listeners={listeners}
          disabled={isDisabled}
        />

        {isCompound ? (
          <div className="replay-condition-card__quantifier">
            <span className="replay-condition-card__when-label">When</span>
            <div className="replay-condition-card__logic-toggle">
              <button
                type="button"
                className={condition.quantifier === ConditionQuantifier.All ? 'active' : ''}
                onClick={() => handleQuantifierToggle(ConditionQuantifier.All)}
                disabled={isDisabled}
                data-testid="quantifier-all"
              >
                ALL
              </button>
              <button
                type="button"
                className={condition.quantifier === ConditionQuantifier.Any ? 'active' : ''}
                onClick={() => handleQuantifierToggle(ConditionQuantifier.Any)}
                disabled={isDisabled}
                data-testid="quantifier-any"
              >
                ANY
              </button>
            </div>
            <span className="replay-condition-card__quantifier-text">of these are true:</span>
          </div>
        ) : (
          <div className="replay-condition-card__single-label">
            <span>When</span>
          </div>
        )}

        <button
          type="button"
          className="btn btn-sm btn-outline-danger replay-condition-card__remove"
          onClick={onRemove}
          disabled={isDisabled}
          title="Remove condition"
          data-testid="remove-condition"
        >
          ×
        </button>
      </div>

      {/* Sub-conditions */}
      <div className="replay-condition-card__sub-conditions">
        {subConditions.map((sub, idx) => (
          <SubConditionRow
            key={sub.uuid}
            subCondition={sub}
            memoryVariables={memoryVariables}
            isDisabled={isDisabled}
            onChange={(updated) => handleSubConditionChange(idx, updated)}
            onRemove={() => handleSubConditionRemove(idx)}
            canRemove={subConditions.length > 1}
          />
        ))}

        <button
          type="button"
          className="btn btn-sm btn-outline-info mt-2 replay-condition-card__add-sub"
          onClick={handleAddSubCondition}
          disabled={isDisabled || !canAddSub}
          data-testid="add-sub-condition"
        >
          + Sub-condition
        </button>
      </div>

      <ReplayResultRow
        condition={condition}
        branches={branches}
        currentBranchId={currentBranchId}
        isDisabled={isDisabled}
        onChange={onChange}
      />
    </div>
  );
}
